import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PINEntry = ({ onSuccess, onCancel, amount, recipient, mode, t, isRTL }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Merge passed translations with defaults so missing keys don't break UI
  const defaults = {
    confirmPayment: 'Confirm Payment',
    confirmTopUp: 'Confirm Top-Up',
    confirmTransfer: 'Confirm Transfer',
    enterPIN: 'Enter your 6-digit PIN',
    incorrectPIN: 'Incorrect PIN. Please try again.',
    demoPIN: 'Demo PIN:',
    verifying: 'Verifying…',
    pinAmountLabel: 'Amount',
    pinMethodLabel: 'Method',
    pinToLabel: 'To',
  };
  const tr = { ...defaults, ...(t || {}) };

  const getTitle = () => {
    if (mode === 'topup') return tr.confirmTopUp;
    if (recipient) return tr.confirmTransfer;
    return tr.confirmPayment;
  };

  const handleNumberPress = (num) => {
    if (verifying) return;
    if (pin.length < 6) {
      const newPin = pin + num;
      setPin(newPin);
      setError('');
      if (newPin.length === 6) {
        setTimeout(() => verifyPin(newPin), 250);
      }
    }
  };

  const handleBackspace = () => {
    if (verifying) return;
    setPin(pin.slice(0, -1));
    setError('');
  };

  const verifyPin = (pinValue) => {
    setVerifying(true);
    // Demo PIN: 123456 succeeds, anything else fails
    if (pinValue === '123456') {
      setTimeout(() => {
        onSuccess();
      }, 350);
    } else {
      setTimeout(() => {
        setError(tr.incorrectPIN);
        setPin('');
        setVerifying(false);
      }, 350);
    }
  };

  return (
    <motion.div
      data-testid="pin-entry-screen"
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#0F0F0F',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr',
        zIndex: 5,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px 8px', flexShrink: 0 }}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onCancel}
          data-testid="pin-back-btn"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '9px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#F5F1EA',
            fontSize: '14px',
            flexShrink: 0,
          }}
        >
          {isRTL ? '→' : '←'}
        </motion.button>
        <h2 style={{ color: '#F5F1EA', fontSize: '15px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px' }}>
          {getTitle()}
        </h2>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '4px 18px 12px', minHeight: 0 }}>
        {/* Transaction summary — slightly larger to balance the keypad */}
        {amount && (
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.04) 100%)',
              border: '1px solid rgba(212,175,55,0.22)',
              borderRadius: '18px',
              padding: '20px 20px',
              marginBottom: '8px',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ color: '#8B93A7', fontSize: '11px', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                {tr.pinAmountLabel}
              </p>
              <p style={{ color: '#D4AF37', fontSize: '26px', fontWeight: '800', margin: 0, letterSpacing: '-0.8px', lineHeight: 1, display: 'flex', alignItems: 'baseline' }}>
                {amount}
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#8B93A7', marginLeft: '5px' }}>MAD</span>
              </p>
            </div>
            {recipient && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p style={{ color: '#8B93A7', fontSize: '11px', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                  {mode === 'topup' ? tr.pinMethodLabel : tr.pinToLabel}
                </p>
                <p style={{ color: '#F5F1EA', fontSize: '15px', margin: 0, fontWeight: '600', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {recipient}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Spacer pushes prompt+dots toward middle */}
        <div style={{ flex: '0.6' }} />

        {/* PIN prompt */}
        <p style={{ color: '#F5F1EA', fontSize: '17px', fontWeight: '700', textAlign: 'center', margin: '0 0 6px', letterSpacing: '-0.3px', flexShrink: 0 }}>
          {tr.enterPIN}
        </p>
        <p style={{ color: '#8B93A7', fontSize: '12px', textAlign: 'center', margin: '0 0 18px', flexShrink: 0 }}>
          {tr.demoPIN} <span style={{ color: '#D4AF37', fontWeight: '600' }}>123456</span>
        </p>

        {/* PIN dots — slightly bigger */}
        <div
          data-testid="pin-dots"
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            marginBottom: '10px',
            minHeight: '18px',
            flexShrink: 0,
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <motion.div
              key={index}
              animate={{
                scale: pin.length === index ? [1, 1.18, 1] : 1,
                backgroundColor: pin.length > index
                  ? (error ? '#FF6464' : '#D4AF37')
                  : 'rgba(255,255,255,0.08)',
              }}
              transition={{ duration: 0.18 }}
              style={{
                width: '15px',
                height: '15px',
                borderRadius: '50%',
                border: pin.length > index ? 'none' : '1.5px solid rgba(255,255,255,0.20)',
              }}
            />
          ))}
        </div>

        {/* Error message — fixed slot */}
        <div style={{ minHeight: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px', flexShrink: 0 }}>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="pin-error"
              style={{ color: '#FF6464', fontSize: '12px', fontWeight: '600', margin: 0, letterSpacing: '-0.1px', textAlign: 'center' }}
            >
              {error}
            </motion.p>
          )}
          {verifying && !error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600', margin: 0 }}
            >
              {tr.verifying}
            </motion.p>
          )}
        </div>

        {/* Spacer pushes keypad to the bottom */}
        <div style={{ flex: '0.4' }} />

        {/* Compact Number Pad — fixed-size buttons, no aspect-ratio so it doesn't grow */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 56px)',
            gap: '8px',
            justifyContent: 'center',
            paddingTop: '4px',
            flexShrink: 0,
          }}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <motion.button
              key={num}
              data-testid={`pin-key-${num}`}
              whileTap={{ scale: 0.92 }}
              onClick={() => handleNumberPress(num.toString())}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#F5F1EA',
                fontSize: '19px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                padding: 0,
              }}
            >
              {num}
            </motion.button>
          ))}

          <div />

          <motion.button
            data-testid="pin-key-0"
            whileTap={{ scale: 0.92 }}
            onClick={() => handleNumberPress('0')}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#F5F1EA',
              fontSize: '19px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              padding: 0,
            }}
          >
            0
          </motion.button>

          <motion.button
            data-testid="pin-key-back"
            whileTap={{ scale: 0.92 }}
            onClick={handleBackspace}
            disabled={pin.length === 0}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: pin.length === 0 ? '#3A3A3A' : '#8B93A7',
              cursor: pin.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M22 11H7" />
              <path d="M11 7l-4 4 4 4" />
            </svg>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default PINEntry;
